/**
 * Affiliate Link Create Modal
 * 
 * Modal for creating new campaign links with:
 * - Campaign name input
 * - Optional custom slug
 * - Optional referral discount override
 * - Live preview of the link
 */

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2Icon,
  CopyIcon,
  CheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useCreateLink } from "../hooks/use-affiliate-data";
import type { Affiliate } from "../types";

import { API_BASE_URL } from "@/backend/config";

// Use the redirect service URL (via Doppler in production)
// This goes through the API to track clicks, then redirects to /invite/:slug
const REDIRECT_BASE_URL = import.meta.env.VITE_REDIRECT_BASE_URL || API_BASE_URL;

type AffiliateLinkCreateModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  affiliate: Affiliate;
};

// Generate a slug from the campaign name
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 25);
}

export const AffiliateLinkCreateModal = ({
  open,
  onOpenChange,
  affiliate,
}: AffiliateLinkCreateModalProps) => {
  const createLink = useCreateLink();
  
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [useCustomDiscount, setUseCustomDiscount] = useState(false);
  const [customDiscount, setCustomDiscount] = useState(affiliate.referralDiscount * 100);
  const [copied, setCopied] = useState(false);

  const maxCommission = affiliate.tradingFeeShare * 100; // 40% - total commission
  const adminBaseRebate = affiliate.referralDiscount * 100; // 10% - admin-set MINIMUM

  // Auto-generate slug from name if not manually edited
  useEffect(() => {
    if (!slugTouched && name) {
      setSlug(generateSlug(name));
    }
  }, [name, slugTouched]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setSlugError(null);
      setDescription("");
      setUseCustomDiscount(false);
      setCustomDiscount(adminBaseRebate);
      setCopied(false);
    }
  }, [open, adminBaseRebate]);

  const handleSlugChange = (value: string) => {
    setSlugTouched(true);
    const lowercased = value.toLowerCase();
    
    // Check for invalid characters
    const invalidChars = lowercased.match(/[^a-z0-9-_]/g);
    if (invalidChars) {
      setSlugError("Only lowercase letters, numbers, hyphens, and underscores allowed");
      // Still sanitize and set the value
      const sanitized = lowercased.replace(/[^a-z0-9-_]/g, "");
      setSlug(sanitized);
    } else {
      setSlugError(null);
      setSlug(lowercased);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Campaign name is required");
      return;
    }

    if (!slug.trim() || slug.length < 3) {
      toast.error("Slug must be at least 3 characters");
      return;
    }

    try {
      await createLink.mutateAsync({
        name: name.trim(),
        slug: slug.toLowerCase(),
        referralDiscount: useCustomDiscount ? (customDiscount / 100).toString() : undefined,
        description: description.trim() || undefined,
      });
      toast.success("Campaign link created!");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create link");
    }
  };

  const previewUrl = `${REDIRECT_BASE_URL}/r/${slug || "your-slug"}`;
  const effectiveRebate = useCustomDiscount ? customDiscount : adminBaseRebate;
  
  // Calculate commission breakdown:
  // - Platform always pays the admin base rebate
  // - If affiliate gives MORE, it comes from their commission
  const extraRebateFromAffiliate = Math.max(0, effectiveRebate - adminBaseRebate);
  const affiliateNetCommission = maxCommission - extraRebateFromAffiliate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Create Campaign Link
          </DialogTitle>
          <DialogDescription>
            Create a trackable link for a specific campaign or channel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Campaign Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Campaign Name *</label>
            <Input
              placeholder="e.g., Twitter Q1, YouTube Banner"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              A descriptive name for this campaign
            </p>
          </div>

          {/* Custom Slug */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Link Slug</label>
            <Input
              placeholder="your-slug"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              maxLength={30}
              className={cn("font-mono", slugError && "border-destructive")}
            />
            {slugError ? (
              <p className="text-xs text-destructive">{slugError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">3-30 characters</p>
            )}
          </div>

          {/* Preview URL */}
          {slug && (
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-3">
              <p className="font-mono text-sm truncate flex-1 min-w-0">{previewUrl}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                className="h-7 w-7 p-0 shrink-0"
              >
                {copied ? (
                  <CheckIcon className="size-3.5 text-emerald-500" />
                ) : (
                  <CopyIcon className="size-3.5" />
                )}
              </Button>
            </div>
          )}

          {/* Custom Discount Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">
                Custom Referral Discount
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUseCustomDiscount(!useCustomDiscount)}
                className={cn(
                  "h-7 text-xs",
                  useCustomDiscount && "bg-primary/10 border-primary/30"
                )}
              >
                {useCustomDiscount ? "Using Custom" : "Set Custom"}
              </Button>
            </div>

            {useCustomDiscount && (
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                {/* Visual Split */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-500/10 rounded-lg p-3 text-center border border-emerald-500/20">
                    <p className="text-xs text-muted-foreground mb-1">Your Net Commission</p>
                    <p className="text-xl font-bold text-emerald-500">
                      {affiliateNetCommission.toFixed(0)}%
                    </p>
                    {extraRebateFromAffiliate > 0 && (
                      <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                        ({maxCommission}% - {extraRebateFromAffiliate}% extra)
                      </p>
                    )}
                    {extraRebateFromAffiliate === 0 && (
                      <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                        (full commission)
                      </p>
                    )}
                  </div>
                  <div className="bg-primary/10 rounded-lg p-3 text-center border border-primary/20">
                    <p className="text-xs text-muted-foreground mb-1">Referral Rebate</p>
                    <p className="text-xl font-bold text-primary">
                      {effectiveRebate.toFixed(0)}%
                    </p>
                    <p className="text-[10px] text-primary/80 mt-0.5">
                      ({adminBaseRebate}% platform{extraRebateFromAffiliate > 0 ? ` + ${extraRebateFromAffiliate}% you` : ""})
                    </p>
                  </div>
                </div>

                {/* Slider */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{adminBaseRebate.toFixed(0)}% (admin minimum)</span>
                    <span>{maxCommission.toFixed(0)}% (give all your commission)</span>
                  </div>
                  <Slider
                    value={[customDiscount]}
                    onValueChange={([value]) => setCustomDiscount(value)}
                    min={adminBaseRebate}
                    max={maxCommission}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Preset Buttons */}
                <div className="flex gap-2">
                  {[adminBaseRebate, 15, 20, 25, maxCommission].filter((preset, idx, arr) => 
                    preset >= adminBaseRebate && preset <= maxCommission && arr.indexOf(preset) === idx
                  ).map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setCustomDiscount(preset)}
                      className={cn(
                        "flex-1 py-1.5 text-xs font-medium rounded border transition-colors",
                        customDiscount === preset
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground"
                      )}
                    >
                      {preset}%
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!useCustomDiscount && (
              <p className="text-xs text-muted-foreground">
                Using admin default: <span className="font-medium">{adminBaseRebate.toFixed(0)}%</span> (platform-paid)
              </p>
            )}
            
            {useCustomDiscount && extraRebateFromAffiliate > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 mt-2">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  You're giving <strong>{extraRebateFromAffiliate.toFixed(0)}%</strong> extra from your commission. 
                  Your net: <strong>{affiliateNetCommission.toFixed(0)}%</strong>
                </p>
              </div>
            )}
          </div>

          {/* Notes (Optional) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes (Optional)</label>
            <Input
              placeholder="Add notes about this campaign..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !slug.trim() || slug.length < 3 || createLink.isPending}
            className="bg-[#2ebd85] hover:bg-[#2ebd85]/90 text-white"
          >
            {createLink.isPending ? (
              <>
                <Loader2Icon className="size-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              "Create Link"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

